/* eslint-disable @typescript-eslint/no-unused-vars */
import type { Address, Hash } from "@keyban/types";
import {
  Asset,
  BASE_FEE,
  Horizon,
  nativeToScVal,
  Operation,
  rpc,
  StrKey,
  Transaction,
  TransactionBuilder,
} from "@stellar/stellar-sdk";
import { BigNumber } from "bignumber.js";
import { Buffer } from "buffer";

import { IKeybanApi } from "~/api";
import {
  EstimateERC20TransferParams,
  EstimateNftTransferParams,
  FeesEstimation,
  KeybanAccount,
  SdkError,
  TransferERC20Params,
  TransferNftParams,
} from "~/index";
import { StellarSigner } from "~/stellar/signer";

const TX_VALIDITY_DURATION = 25; // Slightly below the default polling timeout of 30 seconds
const STELLAR_DECIMALS = 7;
BigNumber.config({
  DECIMAL_PLACES: STELLAR_DECIMALS,
});

/**
 * Stellar-specific {@link KeybanAccount} implementation.
 * Supports native XLM transfers and Soroban token transfers; NFT transfers are not implemented yet.
 */
export class StellarAccount extends KeybanAccount {
  address: string;
  publicKey: string;
  #signer: StellarSigner;
  #rpcProvider: rpc.Server;
  #horizonServer: Horizon.Server;

  constructor(
    api: IKeybanApi,
    rpcProvider: rpc.Server,
    horizonServer: Horizon.Server,
    signer: StellarSigner,
    publicKey: string,
  ) {
    super(api);

    this.#rpcProvider = rpcProvider;
    this.#horizonServer = horizonServer;
    this.#signer = signer;

    this.address = publicKey; // stellar addresses aren't hexa https://stellar.github.io/js-stellar-sdk/Address.html#Address
    this.publicKey = publicKey; // account.accountId == publicKey == address
  }

  /**
   * Sign a message and return a base64-encoded signature.
   * @param message - The message to sign.
   * @returns Base64 signature string.
   * @example
   * const sig = await account.signMessage("hello");
   * console.log(sig);
   */
  async signMessage(message: string): Promise<string | string[]> {
    const hash = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(message),
    );
    const signedMessage = await this.#signer.sign(Buffer.from(hash));
    return signedMessage.toString("base64");
  }

  /**
   * Transfer native XLM to a Stellar address.
   * If the recipient doesn't exist, a createAccount operation is submitted instead.
   * @param to - Recipient public key.
   * @param value - Amount in stroops (1 XLM = 10^7 stroops).
   * @param [fee] - Optional fee per operation in stroops; defaults to network base fee.
   * @returns Transaction hash string.
   * @throws {SdkError} {@link SdkError.types.AmountInvalid} | {@link SdkError.types.InsufficientFunds}
   * @example
   * const tx = await account.transfer("G...DEST", 100_00000n);
   * console.log(tx);
   */
  async transfer(
    to: Address,
    value: bigint,
    fee?: StellarFeeDetails,
  ): Promise<string> {
    if (value <= 0n) {
      throw new SdkError(
        SdkError.types.AmountInvalid,
        "KeybanAccount.transfer",
      );
    }

    const [account, networkDetails] = await Promise.all([
      this.#rpcProvider.getAccount(this.address),
      this.#rpcProvider.getNetwork(),
    ]).catch((err: Error) => {
      if (err.message.includes("Account not found")) {
        throw new SdkError(
          SdkError.types.InsufficientFunds,
          "KeybanAccount.transfer",
        );
      }
      throw new SdkError(
        SdkError.types.UnknownTransactionError,
        "KeybanAccount.transfer",
        err,
      );
    });

    const paymentTxBuilder = new TransactionBuilder(account, {
      fee: fee ?? BASE_FEE,
      networkPassphrase: networkDetails.passphrase,
    });
    const formattedAmount = BigNumber(value)
      .dividedBy(10 ** STELLAR_DECIMALS)
      .toString();
    const recipientExists = await this.#isAccountCreated(to);

    if (recipientExists) {
      paymentTxBuilder.addOperation(
        Operation.payment({
          destination: to,
          asset: Asset.native(),
          amount: formattedAmount,
        }),
      );
    } else {
      paymentTxBuilder.addOperation(
        Operation.createAccount({
          destination: to,
          startingBalance: formattedAmount,
        }),
      );
    }
    const paymentTx = paymentTxBuilder.setTimeout(TX_VALIDITY_DURATION).build();

    // sign the transaction
    const signature = await this.#signer.signDecorated(paymentTx.hash());
    paymentTx.addDecoratedSignature(signature);

    // submit the transaction to the network
    return this.#submitTransaction(paymentTx, "transfer");
  }

  /**
   * Estimate fees for a native XLM transfer.
   * @param _to - Recipient address (unused for fee computation).
   * @returns Estimation where `details` is the fee per operation as a string.
   */
  async estimateTransfer(_to: Address): Promise<FeesEstimation> {
    const operationCount = 1n; // 1 operation for native transfer
    const feesPerOperation = await this.#estimateInclusionFeePerOperation();
    const maxFees = feesPerOperation * operationCount;
    return {
      maxFees,
      details: feesPerOperation.toString(),
    };
  }

  /**
   * Transfer a Soroban token (contract) using `transfer` entrypoint.
   * @param params - Object with `contractAddress`, `to`, `value`, and optional `fees` (stroops per op).
   * @returns Transaction hash string.
   * @throws {SdkError} {@link SdkError.types.AddressInvalid} | {@link SdkError.types.RecipientAddressEqualsSender} | {@link SdkError.types.AmountInvalid} | {@link SdkError.types.InsufficientFunds}
   * @example
   * const tx = await account.transferERC20({ contractAddress: "C...", to: "G...DEST", value: 1_000_000n });
   */
  async transferERC20(
    params: TransferERC20Params<StellarFeeDetails>,
  ): Promise<string> {
    const { contractAddress, to, value, fees } = params;
    if (!StrKey.isValidEd25519PublicKey(to)) {
      throw new SdkError(
        SdkError.types.AddressInvalid,
        "KeybanAccount.transferERC20",
      );
    }

    if (!/^[a-zA-Z0-9]+$/.test(contractAddress)) {
      throw new SdkError(
        SdkError.types.AddressInvalid,
        "KeybanAccount.transferERC20",
      );
    }

    if (to === this.address) {
      throw new SdkError(
        SdkError.types.RecipientAddressEqualsSender,
        "KeybanAccount.transferERC20",
      );
    }

    if (value <= 0n) {
      throw new SdkError(
        SdkError.types.AmountInvalid,
        "KeybanAccount.transferERC20",
      );
    }

    const [account, networkDetails] = await Promise.all([
      this.#rpcProvider.getAccount(this.address),
      this.#rpcProvider.getNetwork(),
    ]).catch((err: Error) => {
      if (err.message.includes("Account not found")) {
        throw new SdkError(
          SdkError.types.InsufficientFunds,
          "KeybanAccount.transferERC20",
        );
      }
      throw new SdkError(
        SdkError.types.UnknownTransactionError,
        "KeybanAccount.transferERC20",
        err,
      );
    });

    const paymentTx = new TransactionBuilder(account, {
      fee: fees ?? BASE_FEE,
      networkPassphrase: networkDetails.passphrase,
    })
      .addOperation(
        Operation.invokeContractFunction({
          contract: contractAddress,
          function: "transfer",
          args: [
            nativeToScVal(this.address, { type: "address" }), // from
            nativeToScVal(to, { type: "address" }), // to
            nativeToScVal(value, {
              type: "i128",
            }), // Amount
          ],
        }),
      )
      .setTimeout(TX_VALIDITY_DURATION)
      .build();

    const preparedTransaction = await this.#rpcProvider
      .prepareTransaction(paymentTx)
      .catch((err: Error) => {
        throw new SdkError(
          SdkError.types.UnknownTransactionError,
          "KeybanAccount.transferERC20",
          err,
        );
      });

    // sign the transaction
    const signature = await this.#signer.signDecorated(
      preparedTransaction.hash(),
    );
    preparedTransaction.addDecoratedSignature(signature);

    // submit the transaction to the network
    return this.#submitTransaction(preparedTransaction, "transferERC20");
  }

  /**
   * Estimate fees for a Soroban token transfer.
   * @param params - Object with `contractAddress`, `to`, `value`.
   * @returns Estimation where `details` is the fee per operation as a string.
   */
  async estimateERC20Transfer(
    params: EstimateERC20TransferParams,
  ): Promise<FeesEstimation> {
    const { contractAddress, to, value } = params;
    const [account, networkDetails] = await Promise.all([
      this.#rpcProvider.getAccount(this.address),
      this.#rpcProvider.getNetwork(),
    ]).catch((err: Error) => {
      if (err.message.includes("Account not found")) {
        throw new SdkError(
          SdkError.types.InsufficientFunds,
          "KeybanAccount.estimateERC20Transfer",
        );
      }
      throw new SdkError(
        SdkError.types.UnknownTransactionError,
        "KeybanAccount.estimateERC20Transfer",
        err,
      );
    });

    const paymentTx = new TransactionBuilder(account, {
      fee: BASE_FEE,
      networkPassphrase: networkDetails.passphrase,
    })
      .addOperation(
        Operation.invokeContractFunction({
          contract: contractAddress,
          function: "transfer",
          args: [
            nativeToScVal(this.address, { type: "address" }), // from
            nativeToScVal(to, { type: "address" }), // to
            nativeToScVal(value, {
              type: "i128",
            }), // Amount
          ],
        }),
      )
      .setTimeout(TX_VALIDITY_DURATION)
      .build();
    const simulation = await this.#rpcProvider
      .simulateTransaction(paymentTx)
      .catch((err: Error) => {
        throw new SdkError(
          SdkError.types.UnknownTransactionError,
          "KeybanAccount.estimateERC20Transfer",
          err,
        );
      });
    if ((simulation as rpc.Api.SimulateTransactionErrorResponse).error) {
      throw new SdkError(
        SdkError.types.UnknownTransactionError,
        "KeybanAccount.estimateERC20Transfer",
        {
          simulationError: (
            simulation as rpc.Api.SimulateTransactionErrorResponse
          ).error,
        },
      );
    }

    const successfulSimulation =
      simulation as rpc.Api.SimulateTransactionRestoreResponse;
    const inclusionFees = await this.#estimateSorobanInclusionFee();
    return {
      maxFees:
        BigInt(successfulSimulation.minResourceFee) +
        BigInt(successfulSimulation.restorePreamble?.minResourceFee ?? 0) +
        inclusionFees,
      details: inclusionFees.toString(),
    };
  }

  /**
   * Not implemented on Stellar.
   * @param _params - NFT transfer parameters.
   * @throws {Error} Always throws because NFTs aren't supported here.
   */
  transferNft(_params: TransferNftParams<StellarFeeDetails>): Promise<Hash> {
    throw new Error("transferNft not implemented.");
  }

  /**
   * Not implemented on Stellar.
   * @param _params - NFT estimation parameters.
   * @throws {Error} Always throws because NFTs aren't supported here.
   */
  estimateNftTransfer(
    _params: EstimateNftTransferParams,
  ): Promise<FeesEstimation> {
    throw new Error("estimateNftTransfer not implemented.");
  }

  async #submitTransaction(
    tx: Transaction,
    functionName: string,
  ): Promise<string> {
    return this.#rpcProvider
      .sendTransaction(tx)
      .then(async (response) => {
        if (response.status !== "PENDING") {
          if (JSON.stringify(response).includes("txInsufficientBalance")) {
            throw new SdkError(
              SdkError.types.InsufficientFunds,
              `KeybanAccount.${functionName}`,
            );
          }
          throw new SdkError(
            SdkError.types.UnknownTransactionError,
            `KeybanAccount.${functionName}`,
            {
              SendTransactionResponse:
                response.errorResult?.result() ?? response,
            },
          );
        }
        return this.#rpcProvider.pollTransaction(response.hash);
      })
      .then((finalResponse) => {
        if (JSON.stringify(finalResponse).includes("txInsufficientBalance")) {
          throw new SdkError(
            SdkError.types.InsufficientFunds,
            `KeybanAccount.${functionName}`,
          );
        }
        switch (finalResponse.status) {
          case rpc.Api.GetTransactionStatus.NOT_FOUND:
            throw new SdkError(
              SdkError.types.UnknownTransactionError,
              `KeybanAccount.${functionName}`,
              {
                transactionResponse: JSON.stringify(finalResponse),
              },
            );
          case rpc.Api.GetTransactionStatus.FAILED:
            throw new SdkError(
              SdkError.types.UnknownTransactionError,
              `KeybanAccount.${functionName}`,
              {
                transactionResponse: finalResponse.resultXdr.result(),
              },
            );
          case rpc.Api.GetTransactionStatus.SUCCESS:
            return finalResponse.txHash;
        }
      })
      .catch((err: Error) => {
        throw new SdkError(
          SdkError.types.UnknownTransactionError,
          `KeybanAccount.${functionName}`,
          err,
        );
      });
  }

  async #isAccountCreated(address: string): Promise<boolean> {
    return this.#rpcProvider
      .getAccount(address)
      .then(() => true)
      .catch((err: Error) => {
        if (err.message.includes("Account not found")) {
          return false;
        }
        throw err;
      });
  }

  async #estimateSorobanInclusionFee(): Promise<bigint> {
    const { sorobanInclusionFee } = await this.#rpcProvider.getFeeStats();
    return BigInt(sorobanInclusionFee.p90);
  }

  async #estimateInclusionFeePerOperation(): Promise<bigint> {
    const {
      fee_charged: feeCharged,
      ledger_capacity_usage: ledgerCapacityUsage,
    } = await this.#horizonServer.feeStats();
    const capacityUsage = parseFloat(ledgerCapacityUsage);
    // Select percentile based on mode and network congestion
    if (capacityUsage < 0.7) {
      // Low congestion
      return BigInt(feeCharged.p50);
    } else if (capacityUsage < 0.9) {
      // Medium congestion
      return BigInt(feeCharged.p70);
    } else {
      // High congestion
      return BigInt(feeCharged.p90);
    }
  }
}

/**
 * Stellar fee per operation represented as a string in stroops.
 * In congestion, Horizon exposes a higher mode fee; otherwise it's the base fee (100 stroops).
 * @example
 * const fee: StellarFeeDetails = "100"; // 100 stroops per operation
 */
export type StellarFeeDetails = string;
